import { useAuth } from '../contexts/AuthContext'
import { API_BASE_URL } from '../config'

export type MatterType = 'Sale' | 'Purchase' | 'Transfer' | 'Subdivision';

export interface Matter {
  id: string;
  tenantId: string;
  matterType: MatterType;
  primaryClientId: string;
  secondaryClientId?: string | null;
  propertyAddress: string;
  settlementDate?: string | null;
  status: 'active' | 'closed';
  createdAt: string;
  closedAt?: string;
  [key: string]: unknown;
}

export interface Task {
  id: string;
  matterId: string;
  tenantId: string;
  title: string;
  order: number;
  dueDate?: string | null;
  status: 'pending' | 'completed';
  createdAt: string;
  completedAt?: string;
  [key: string]: unknown;
}

export interface CreateMatterPayload {
  matterType: MatterType;
  primaryClientId: string;
  secondaryClientId?: string;
  propertyAddress: string;
  settlementDate?: string;
}

export interface CreateTaskPayload {
  title: string;
  dueDate?: string;
}

export const useMatterService = () => {
  const { token, authFetch } = useAuth()

  const listMatters = async (): Promise<Matter[]> => {
    if (!token) {
      return []
    }

    const response = await authFetch(`${API_BASE_URL}/matters`)
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to list matters: ${response.status}`)
    }

    return data.matters || []
  }

  const createMatter = async (payload: CreateMatterPayload): Promise<{ matter: Matter; tasks: Task[] }> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/matters`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to create matter: ${response.status}`)
    }

    return data
  }

  const closeMatter = async (matterId: string): Promise<Matter> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/matters/${matterId}/close`, {
      method: 'POST',
    })
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to close matter: ${response.status}`)
    }

    return data.matter
  }

  const listTasks = async (matterId: string): Promise<Task[]> => {
    if (!token) {
      return []
    }

    const response = await authFetch(`${API_BASE_URL}/matters/${matterId}/tasks`)
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to list tasks: ${response.status}`)
    }

    return data.tasks || []
  }

  const createTask = async (matterId: string, payload: CreateTaskPayload): Promise<Task> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/matters/${matterId}/tasks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to create task: ${response.status}`)
    }

    return data.task
  }

  const completeTask = async (matterId: string, taskId: string): Promise<Task> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/matters/${matterId}/tasks/${taskId}/complete`, {
      method: 'POST',
    })
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to complete task: ${response.status}`)
    }

    return data.task
  }

  return { listMatters, createMatter, closeMatter, listTasks, createTask, completeTask }
}
